import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiFundingData,
  type EnrollmentMultiFundingSubmitInput,
  type EnrollmentMultiFundingSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Funding data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiFundingData>
 * @example const response = await getEnrollmentMultiFundingData()
 */
export const getEnrollmentMultiFundingData = async (context?: DefaultContext): Promise<EnrollmentMultiFundingData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiFundingData',
      query: `mutation getEnrollmentMultiFundingData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              fundingAccountName
              transferFundsInitiated
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiFundingData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiFundingData = createQueryHook<undefined, EnrollmentMultiFundingData>(
  getEnrollmentMultiFundingData,
)

/**
 * Submit Enrollment Multi Funding data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiFundingSubmitOutput>
 * @example const response = await submitEnrollmentMultiFundingData();
 */
export const submitEnrollmentMultiFundingData = async (
  enrollmentFundingSubmitInput: EnrollmentMultiFundingSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiFundingSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiFundingData',
      query: `mutation submitEnrollmentMultiFundingData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFundingSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiFundingSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiFundingData = createMutationHook<
  EnrollmentMultiFundingSubmitInput,
  EnrollmentMultiFundingSubmitOutput
>(submitEnrollmentMultiFundingData)
